import { navigateBackOrFallback } from "../shared/screen-shell/screen-navigation.utils";
import { AuthService } from "@auth0/auth0-angular";
import { firstValueFrom, Subject, EMPTY } from "rxjs";
import { switchMap, catchError, takeUntil } from "rxjs/operators";
import { AuthenticatedRealtimeService } from "../shared/services/authenticated-realtime.service";
import { DialogService } from "../core/dialog/dialog.service";
import { ChangeDetectorRef, Component, ElementRef, EventEmitter, NgZone, OnDestroy, OnInit, Output, ViewChild } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { Message } from "../shared/models/message";
import { PlayerService } from "../shared/services/player.service";
import { Player } from "../shared/models/player";
import { ChatService } from "../shared/services/chat.service";
import { MessageService } from "../shared/services/message.service";
import { Chat } from "../shared/models/chat";
import { ChatUtils } from "../shared/utils/chat.utils";
import { environment } from "../environments/environment";
import { formatDate, Location } from "@angular/common";
import { DelayedSpinnerService } from "../shared/services/delayed-spinner.service";
import { AnalyticsService } from "../shared/services/analytics.service";
import { BehaviorSubject, of } from "rxjs";
import { SentStatus } from "../shared/enum/sent-status";
import { LocaleService } from "../shared/i18n/locale.service";
import { TranslateService } from "@ngx-translate/core";

@Component({
    selector: "messages",
    templateUrl: "./chat.component.html",
    styleUrls: ["./chat.component.css"],
    standalone: false
})
export class ChatComponent implements OnInit, OnDestroy {
  @ViewChild("scrollMe")
  public myScrollContainer: ElementRef;

  @Output()
  public noMessagesEmitter: EventEmitter<boolean> = new EventEmitter<boolean>();

  public messages: Message[];
  public messagesBySenderGrouped: Message[] = [];
  public transformedMessages: any[];
  public selectedChatName: string;

  public newMessageText: string;

  public throttle = 200;
  public scrollDownDistance = 1;
  public scrollUpDistance = 3;

  public chat: Chat;
  public viewedByManager: boolean = false;

  public loading = false;
  public readonly chatReady$ = new BehaviorSubject<boolean>(false);
  public scrollDateLabel: string = "";
  public showScrollDateLabel: boolean = false;

  private selectedChatId: string;
  public currentPlayer: Player;
  private unreadMessagesCount: number;
  private newChatLoadInProgress: boolean = false;

  private loadedPageIndex: number = 0;
  private resultsPerPage: number = 100;
  private chatInitialLoad: boolean = false;
  private nextBeforeCreated: number = null;
  public hasMoreMessages: boolean = true;

  private stopRealtime: () => void;
  private readonly destroy$ = new Subject<void>();
  private readonly refreshAccess$ = new Subject<void>();
  private accessLost = false;
  private socket: WebSocket;
  private wsUrl: string = environment.WEB_SOCKET_URL;
  private destroyed = false;
  private suppressSocketReconnect = false;
  private scrollDateHideTimeout: number = null;

  constructor(
    public chatUtils: ChatUtils,
    public playerService: PlayerService,
    public chatService: ChatService,
    public messageService: MessageService,
    public router: Router,
    public cdRef: ChangeDetectorRef,
    public zone: NgZone,
    public route: ActivatedRoute,
    private _location: Location,
    private spinner: DelayedSpinnerService,
    private analytics: AnalyticsService,
    private localeService: LocaleService,
    private translate: TranslateService,
    private auth: AuthService,
    private realtime: AuthenticatedRealtimeService,
    private dialogs: DialogService
  ) {}

  private messageCatchupRunning = false;
  private messageCatchupPending = false;

  private async catchUpMessages(): Promise<void> {
    if (!this.selectedChatId || this.destroyed || this.accessLost) return;
    if (this.messageCatchupRunning) { this.messageCatchupPending = true; return; }
    this.messageCatchupRunning = true;
    const chatId = this.selectedChatId;
    const playerId = this.currentPlayer?.id;
    const current = () => !this.destroyed && !this.accessLost && this.selectedChatId === chatId && this.playerService.getCurrentPlayer()?.id === playerId;
    const known = new Set((this.messages || []).map(message => message.id).filter(Boolean));
    const received: Message[] = [];
    let before: number = null;
    try {
      while (current()) {
        const page = await firstValueFrom(this.messageService.fetchChatMessagePage(chatId, before, this.resultsPerPage));
        if (!current()) return;
        const items = page.items || [];
        received.push(...items);
        if (!items.length || items.some(message => known.has(message.id)) || !page.hasMore || !page.nextBeforeCreated || page.nextBeforeCreated === before || !known.size) break;
        before = page.nextBeforeCreated;
      }
      if (!current()) return;
      for (const message of received.reverse()) {
        this.chatUtils.visualizeMessage(message);
        this.appendMessageIfNew(message);
      }
      this.messages.sort((a, b) => a.created - b.created);
      this.transformMessages(this.messages);
      if (document.visibilityState !== 'hidden') this.markCurrentChatRead();
    } catch (error) {
      if (current() && [403, 404].includes(error?.status)) this.handleAccessLost();
    } finally {
      this.messageCatchupRunning = false;
      if (this.messageCatchupPending) {
        this.messageCatchupPending = false;
        if (current()) this.catchUpMessages();
      }
    }
  }

  ngOnInit() {
    this.cdRef.reattach();
    this.refreshAccess$.pipe(switchMap(() => this.selectedChatId ? this.chatService.getChatById(this.selectedChatId, false).pipe(catchError(error => {
      if ([403, 404].includes(error?.status)) this.handleAccessLost();
      return EMPTY;
    })) : EMPTY), takeUntil(this.destroy$)).subscribe(chat => { this.chat = chat; });
    this.stopRealtime = this.realtime.subscribe(({type, data}) => {
      if (type === 'connected' || (type === 'chat.message' && data?.chatId === this.selectedChatId)) this.catchUpMessages();
      if (type === 'connected' || data?.chatId === this.selectedChatId || (data?.eventId === this.chat?.entityId && ['participants.changed', 'event.deleted'].includes(type))) this.refreshAccess$.next();
    });

    this.loading = true;
    this.chatReady$.next(false);
    this.spinner.show();
    this.currentPlayer = this.playerService.getCurrentPlayer();

    let chatId = this.route.snapshot.params["chatId"];
    let entityId = this.route.snapshot.params["entityId"];

    const prepared = this.route.snapshot.data['screen'];
    if (prepared?.chat) {
      this.chat = prepared.chat;
      this.viewedByManager = this.chat.contact?.id === this.currentPlayer?.id;
      this.changeDisplayName();
      this.chatTab(this.chat, prepared.page);
    } else if (chatId) {
      this.loadMessagesByChatId(chatId);
    } else {
      this.loadMessagesByEventId(entityId);
    }

    this.newMessageText = "";
    this.loadedPageIndex = 1;

    this.setUnreadMessagesCount(0);
    this.chatInitialLoad = true;

  }

  public openDirectChatFromMessage(player: Player): void {
    if (!player || !player.id || !this.currentPlayer || player.id === this.currentPlayer.id) {
      return;
    }
    this.chatService.openDirectChat(player.id).subscribe(
      (thread) => {
        if (thread && thread.chat && thread.chat.id) {
          this.switchToChat(thread.chat.id);
        }
      },
      (error) => {
        console.log(error);
      }
    );
  }

  public isOwnMessage(message: Message): boolean {
    return !!(message && message.sender && this.currentPlayer && message.sender.id === this.currentPlayer.id);
  }

  scrollToBottom(): void {
    try {
      this.myScrollContainer.nativeElement.scrollTop = this.myScrollContainer.nativeElement.scrollHeight;
      this.setUnreadMessagesCount(0);
    } catch (err) {
      console.log("scrollToBottom() error: " + err);
    }
  }

  public onScrollUp(): void {
    if (this.newChatLoadInProgress) {
      return;
    }
    console.info("[chat] scrolledUp event", {
      chatId: this.selectedChatId,
      hasMoreMessages: this.hasMoreMessages,
      nextBeforeCreated: this.nextBeforeCreated
    });
    this.fetchAdditionalMessages(this.selectedChatId);
  }

  public onMessageScroll(): void {
    if (!this.myScrollContainer || !this.myScrollContainer.nativeElement) {
      return;
    }
    const scrollElement = this.myScrollContainer.nativeElement;
    this.updateScrollDateIndicator(scrollElement);
    if (scrollElement.scrollTop > 80 || this.newChatLoadInProgress || !this.hasMoreMessages) {
      return;
    }
    console.info("[chat] top threshold reached", {
      scrollTop: scrollElement.scrollTop,
      scrollHeight: scrollElement.scrollHeight,
      clientHeight: scrollElement.clientHeight,
      chatId: this.selectedChatId,
      nextBeforeCreated: this.nextBeforeCreated
    });
    this.fetchAdditionalMessages(this.selectedChatId);
  }

  public onScrollDown(): void {
    this.setUnreadMessagesCount(0);
  }

  ngOnDestroy() {
    this.destroyed = true;
    this.stopRealtime?.();
    this.destroy$.next();
    this.destroy$.complete();
    if (this.cdRef) {
      this.cdRef.detach();
    }

    if (this.socket) {
      this.socket.close();
    }
    if (this.scrollDateHideTimeout) {
      window.clearTimeout(this.scrollDateHideTimeout);
    }
  }

  public modifyChat(): void {
    localStorage.setItem("chat", JSON.stringify(this.chat));
    this.router.navigate(["/chat-detail"]);
  }

  public openChatSettings(): void {
    if (this.chat && this.chat.id) {
      this.router.navigate(["/chat-settings", { chatId: this.chat.id }]);
    }
  }

  public openMatchScreen(): void {
    if (this.chat && this.chat.entityType === "EVENT" && this.chat.entityId) {
      this.router.navigate(["/event-players/false", { eventId: this.chat.entityId }]);
    }
  }

  public leaveChat(): void {
    if (!this.chat || this.chat.entityType === "EVENT") return;
    this.loading = true;
    this.spinner.show();
    this.chatService.leaveChat(this.chat, this.currentPlayer).subscribe(
      (res) => {
        this.router.navigate(["/chat-list"]);
        this.spinner.hide();
        this.loading = false;
      },
      (res) => {
        console.log(res.error);
        this.spinner.hide();
        this.loading = false;
      }
    );
  }

  public submitNewMessage(newMessage: string): void {
    if (this.accessLost) return;
    let me = this;
    const trimmedMessage = newMessage == null ? "" : newMessage.trim();

    if (trimmedMessage.length < 1) {
      me.newMessageText = "";
      return;
    }

    let message: Message = new Message();
    message.localId = crypto.randomUUID();
    message.clientMessageId = message.localId;
    message.chatId = me.selectedChatId;
    message.sender = me.currentPlayer;
    message.createdDateString = new Date().toISOString();
    message.likesCount = 0;
    message.dislikesCount = 0;
    message.text = trimmedMessage;
    message.sent = SentStatus.NOT_SENT;

    me.newMessageText = "";
    me.chatUtils.visualizeMessage(message);
    me.appendMessageIfNew(message);
    me.setUnreadMessagesCount(0);
    me.scrollToBottom();
    setTimeout(() => me.scrollToBottom(), 0);

    me.messageService.sendMessage(message).subscribe(
      (savedMessage) => {
        savedMessage.localId = message.localId;
        savedMessage.sent = SentStatus.SENT;
        me.chatUtils.visualizeMessage(savedMessage);
        me.appendMessageIfNew(savedMessage);
        me.trackChatMessageSent();
        me.setUnreadMessagesCount(0);
        me.scrollToBottom();
      },
      (error) => {
        me.messages = me.messages.filter(item => item.localId !== message.localId);
        me.transformMessages(me.messages);
        me.newMessageText = trimmedMessage;
        if ([403, 404].includes(error?.status)) me.handleAccessLost();
        else me.dialogs.alert({title: me.translate.instant('common.alert'), text: me.translate.instant('chat.sendFailed')});
      }
    );

  }

  public sendNewMessageFromPointer(event: Event): void {
    event.preventDefault();
    this.submitNewMessage(this.newMessageText);
  }

  private trackChatMessageSent(): void {
    this.analytics.track("chat_message_sent", {
      screen: "chat",
      entityType: this.chat && this.chat.entityType ? this.chat.entityType.toLowerCase() : "chat",
      entityId: this.chat && this.chat.entityId ? this.chat.entityId : this.selectedChatId
    });
  }

  public captureBlurEventOnMessageSubmit(newMessage: string): void {
    let _iOSDevice = !!navigator.userAgent.match(/iPhone|iPod|iPad/);
    if (_iOSDevice) {
      this.submitNewMessage(newMessage);
    }
  }

  private loadMessagesByEventId(eventId: string) {
    this.chatService.getChatForEntityId(eventId).subscribe(
      (chat) => {
        this.viewedByManager = !!(chat && chat.contact && this.currentPlayer.id === chat.contact.id);

        this.chat = chat;
        this.changeDisplayName();
        this.chatTab(chat);
      },
      (error) => {
        console.log(error);
        this.spinner.hide();
        this.loading = false;
        this.chatReady$.next(true);
      }
    );
  }

  private loadMessagesByChatId(chatId: string) {
    this.chatService.getChatById(chatId).subscribe(
      (chat) => {
        this.viewedByManager = !!(chat && chat.contact && this.currentPlayer.id === chat.contact.id);

        this.chat = chat;
        this.changeDisplayName();
        this.chatTab(chat);
      },
      (error) => {
        console.log(error);
        this.spinner.hide();
        this.loading = false;
        this.chatReady$.next(true);
      }
    );
  }

  private fetchAdditionalMessages(chatId: string): void {
    let me = this;
    if (!chatId || !me.hasMoreMessages || me.newChatLoadInProgress) {
      console.info("[chat] older messages skipped", {
        chatId,
        hasMoreMessages: me.hasMoreMessages,
        loadingOlder: me.newChatLoadInProgress,
        nextBeforeCreated: me.nextBeforeCreated
      });
      return;
    }
    me.newChatLoadInProgress = true;
    me.spinner.show();
    me.loading = true;

    const startingScrollTop = this.myScrollContainer.nativeElement.scrollTop;
    const startingScrollHeigh = this.myScrollContainer.nativeElement.scrollHeight;
    console.log("1. height: " + this.myScrollContainer.nativeElement.scrollHeight + ", top: " + this.myScrollContainer.nativeElement.scrollTop);
    console.info("[chat] loading older messages", {
      chatId,
      beforeCreated: me.nextBeforeCreated,
      limit: me.resultsPerPage
    });

    me.messageService.fetchChatMessagePage(chatId, me.nextBeforeCreated, me.resultsPerPage).subscribe(
      (messagePage) => {

        let messagesList = messagePage.items || [];

        me.nextBeforeCreated = messagePage.nextBeforeCreated;
        me.hasMoreMessages = messagePage.hasMore;
        console.info("[chat] older messages loaded", {
          returned: messagesList.length,
          nextBeforeCreated: me.nextBeforeCreated,
          hasMoreMessages: me.hasMoreMessages
        });

        if (messagesList === null || messagesList.length === 0) {
          me.spinner.hide();
          me.loading = false;
          me.newChatLoadInProgress = false;
          me.noMessagesEmitter.emit(true);

          return;
        }

        messagesList = messagesList.reverse();
        for (let message of messagesList) {
          if (message.sender.id === me.currentPlayer.id && !message.sender.avatar && me.currentPlayer.avatar) {
            message.sender.avatar = me.currentPlayer.avatar;
          }

          me.chatUtils.visualizeMessage(message);
          me.spinner.hide();
          me.loading = false;
        }

        me.messages = messagesList.concat(this.messages);

        this.transformMessages(me.messages);

        me.loadedPageIndex += 1;

        setTimeout(() => {
          const newScrollHeight = this.myScrollContainer.nativeElement.scrollHeight;
          const diffScrollHeight = newScrollHeight-startingScrollHeigh;
          const newTop = diffScrollHeight+startingScrollTop;
          // set the scroll height from the difference of the new and starting scroll height
          console.log("2. height: " + newScrollHeight + ", diffScrollHeight: " + diffScrollHeight, ", newTop: " + newTop);
          this.myScrollContainer.nativeElement.scrollTo(0, newTop);
          console.log("3. height: " + this.myScrollContainer.nativeElement.scrollHeight + ", top: " + this.myScrollContainer.nativeElement.scrollTop);
          me.newChatLoadInProgress = false;
        }, 1);    
      },
      (error) => {
        console.log(error);
        this.spinner.hide();
        me.loading = false;
        me.newChatLoadInProgress = false;
      }
    );
  }

  private iOS(): boolean {
    let iDevices = ["iPad Simulator", "iPhone Simulator", "iPod Simulator", "iPad", "iPhone", "iPod"];

    if (!!navigator.platform) {
      while (iDevices.length) {
        if (navigator.platform === iDevices.pop()) {
          return true;
        }
      }
    }

    return false;
  }

  private initializeWebSocket(): void {
    let me = this;
    if (this.destroyed || !this.selectedChatId) {
      return;
    }

    me.socket = new WebSocket(`${this.wsUrl}/name?${this.selectedChatId}`, []);
    const socket = me.socket;
    me.suppressSocketReconnect = false;

    me.socket.addEventListener("open", async (event) => {
      try {
        const accessToken = await firstValueFrom(me.auth.getAccessTokenSilently());
        if (socket !== me.socket || me.destroyed || socket.readyState !== WebSocket.OPEN) return;
        socket.send(JSON.stringify({type: 'authenticate', accessToken}));
        me.catchUpMessages();
      } catch { socket.close(); }
      me.zone.run(() => {
        me.scrollToBottom();
      });
    });

    me.socket.addEventListener("message", (event) => {
      me.zone.run(() => {
        let receivedComment: Message = JSON.parse(event.data);
        me.chatUtils.visualizeMessage(receivedComment);

        me.appendMessageIfNew(receivedComment);
        me.markCurrentChatRead();

        setTimeout(() => {
          me.scrollToBottom();
        }, 100);
      });
    });

    me.socket.addEventListener("close", (event) => {
      console.log("Close event raised. Event details: " + JSON.stringify(event));

      me.zone.run(() => {
        if (me.destroyed || me.suppressSocketReconnect || socket !== me.socket) {
          return;
        }
        if (event.code === 1008) { me.refreshAccess$.next(); }
        setTimeout(() => {
          if (me.destroyed || me.accessLost || socket !== me.socket) return;
          me.initializeWebSocket();
        }, 2000);
      });
    });

    me.socket.addEventListener("error", (event) => {
      console.log("ChatDetailComponent -> wsError... The socket had an error", event);
    });
  }

  private handleAccessLost(): void {
    if (this.destroyed || this.accessLost) return;
    this.accessLost = true;
    this.suppressSocketReconnect = true;
    this.socket?.close();
    this.messages = [];
    this.transformMessages([]);
    this.dialogs.alert({title: this.translate.instant('common.alert'), text: this.translate.instant('chat.accessLost')});
    this.router.navigate(['/chat-list']);
  }

  private chatTab(chat: Chat, preparedPage?: any): void {
    let me = this;

    me.loading = true;
    me.spinner.show();
    me.loadedPageIndex = 1;
    me.nextBeforeCreated = null;
    me.hasMoreMessages = true;
    me.chatReady$.next(false);
    me.messages = Array<Message>();
    (preparedPage ? of(preparedPage) : me.messageService.fetchChatMessagePage(chat.id, null, me.resultsPerPage)).subscribe((messagePage) => {
      let messagesList = messagePage.items || [];
      me.nextBeforeCreated = messagePage.nextBeforeCreated;
      me.hasMoreMessages = messagePage.hasMore;
      if (messagesList !== null && messagesList.length > 0) {
        me.messages = messagesList.reverse();

        for (let message of messagesList) {
          me.chatUtils.visualizeMessage(message);
        }
        this.transformMessages(me.messages);
      }

      me.selectedChatId = chat.id;
      me.selectedChatName = chat.name;
      me.unreadMessagesCount = 0;
      me.markCurrentChatRead();

      me.initializeWebSocket();

      setTimeout(function () {
        me.scrollToBottom();
      }, 0);
      me.spinner.hide();
      me.loading = false;
      me.chatReady$.next(true);
    });
  }

  private switchToChat(chatId: string): void {
    if (!chatId || chatId === this.selectedChatId) {
      return;
    }
    this.suppressSocketReconnect = true;
    if (this.socket) {
      this.socket.close();
    }
    this.router.navigate(["/messages", { chatId }]);
    this.loadMessagesByChatId(chatId);
  }

  private setUnreadMessagesCount(messagesCount: number): void {
    this.unreadMessagesCount = messagesCount;
  }

  private markCurrentChatRead(): void {
    if (!this.selectedChatId) {
      return;
    }
    this.chatService.markRead(this.selectedChatId).subscribe(
      () => this.setUnreadMessagesCount(0),
      (error) => console.log(error)
    );
  }

  public goBack(): void {
    navigateBackOrFallback(this._location, this.router, ["/chat-list"]);
  }

  public splitByAndRemoveOther(string, needle, tagname, className, returnNeedle) {
    var array = string.split(needle),
      toReturn = "";
    for (var i = 0; i < array.length; i++) {
      if (array[i].trim() != this.currentPlayer.nickName) {
        toReturn += "<" + tagname + ' class="' + className + '">' + array[i] + "</" + tagname + ">";
      }
    }
    return toReturn;
  }

  public getMessageDateMode(createdDate: number): string {
    let mode = "";
    let current = Date.now();
    let todayMS = current % 86400000;

    if (current - todayMS > createdDate + 518400000) {
      return (mode = "6Days");
    }

    if (current - todayMS > createdDate + 86400000) {
      return (mode = "Days");
    }
    if (current - todayMS > createdDate) {
      return (mode = "Yesterday");
    }
    if (current - todayMS < createdDate) {
      return (mode = "Today");
    }
  }

  public usesInlineMessageTime(message: Message): boolean {
    if (!message || !message.text) {
      return false;
    }
    const text = message.text.trim();
    const isSingleLine = text.indexOf("\n") < 0;
    const hasShortTimeLabel = this.getMessageDateMode(message.created) === "Today";

    return isSingleLine && hasShortTimeLabel && text.length <= 18;
  }

  private updateScrollDateIndicator(scrollElement: HTMLElement): void {
    const created = this.getFirstVisibleMessageCreated(scrollElement);
    if (!created) {
      return;
    }

    this.scrollDateLabel = this.getScrollDateLabel(created);
    this.showScrollDateLabel = true;

    if (this.scrollDateHideTimeout) {
      window.clearTimeout(this.scrollDateHideTimeout);
    }
    this.scrollDateHideTimeout = window.setTimeout(() => {
      this.showScrollDateLabel = false;
      this.scrollDateHideTimeout = null;
    }, 900);
  }

  private getFirstVisibleMessageCreated(scrollElement: HTMLElement): number {
    const containerRect = scrollElement.getBoundingClientRect();
    const targetTop = containerRect.top + 18;
    const targetBottom = containerRect.bottom;
    const bubbles = Array.prototype.slice.call(scrollElement.querySelectorAll(".chat-bubble[data-created]")) as HTMLElement[];

    for (let bubble of bubbles) {
      const bubbleRect = bubble.getBoundingClientRect();
      if (bubbleRect.bottom >= targetTop && bubbleRect.top <= targetBottom) {
        return Number(bubble.getAttribute("data-created"));
      }
    }

    return null;
  }

  private getScrollDateLabel(created: number): string {
    const messageDate = new Date(created);
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const yesterday = new Date(today);
    yesterday.setDate(today.getDate() - 1);

    const weekWindow = new Date(today);
    weekWindow.setDate(today.getDate() - 6);

    const locale = this.localeService.currentLanguage === "hr" ? "hr-HR" : "en-US";
    if (messageDate >= today) {
      return this.translate.instant("chat.today");
    }
    if (messageDate >= yesterday) {
      return this.translate.instant("chat.yesterday");
    }
    if (messageDate >= weekWindow) {
      return formatDate(messageDate, "EEEE", locale);
    }
    return formatDate(messageDate, "mediumDate", locale);
  }

  public transformMessages(messages: Message[]): any {
    var previousMessageSenderId = null;
    this.messagesBySenderGrouped = Array<Message>();

    messages.forEach((message, index) => {
      if(message.sender.id !== previousMessageSenderId){
        message.messages = Array<Message>();
        message.messages.push(message);
        this.messagesBySenderGrouped.push(message);
      }else{      
        this.messagesBySenderGrouped[this.messagesBySenderGrouped.length-1].messages.push(message);
      }

      previousMessageSenderId = message.sender.id;
    });
  }

  private appendMessageIfNew(message: Message): void {
    if (!message) {
      return;
    }
    if (!this.messages) {
      this.messages = Array<Message>();
    }
    if (message.clientMessageId && message.id) {
      this.messages = this.messages.filter(existing => existing.id !== message.id &&
        !(existing.sender?.id === message.sender?.id &&
          (existing.clientMessageId || existing.localId) === message.clientMessageId));
      this.messages.push(this.markMessageSent(message));
      this.transformMessages(this.messages);
      return;
    }
    const existingIndex = this.findExistingMessageIndex(message);
    if (existingIndex >= 0) {
      this.messages[existingIndex] = this.markMessageSent(message);
      this.transformMessages(this.messages);
      return;
    }
    this.messages.push(message);
    this.pushMessageInTransform(message);
  }

  private findExistingMessageIndex(message: Message): number {
    if (!message || !this.messages) {
      return -1;
    }
    if (message.localId) {
      const localIndex = this.messages.findIndex((existing) => existing.localId === message.localId);
      if (localIndex >= 0) {
        return localIndex;
      }
    }
    if (message.id) {
      const idIndex = this.messages.findIndex((existing) => existing.id === message.id);
      if (idIndex >= 0) {
        return idIndex;
      }
    }
    return this.messages.findIndex((existing) => this.isPendingMatchForConfirmedMessage(existing, message));
  }

  private isPendingMatchForConfirmedMessage(pendingMessage: Message, confirmedMessage: Message): boolean {
    if (!pendingMessage || !confirmedMessage || !pendingMessage.localId || !confirmedMessage.id) {
      return false;
    }
    if (pendingMessage.sent !== SentStatus.NOT_SENT) {
      return false;
    }
    if (pendingMessage.chatId !== confirmedMessage.chatId) {
      return false;
    }
    const pendingSenderId = pendingMessage.sender && pendingMessage.sender.id;
    const confirmedSenderId = confirmedMessage.sender && confirmedMessage.sender.id;
    if (!pendingSenderId || pendingSenderId !== confirmedSenderId) {
      return false;
    }
    if ((pendingMessage.text || "").trim() !== (confirmedMessage.text || "").trim()) {
      return false;
    }
    return Math.abs(Number(pendingMessage.created) - Number(confirmedMessage.created)) < 30000;
  }

  private markMessageSent(message: Message): Message {
    message.sent = SentStatus.SENT;
    return message;
  }

  public isPendingMessage(message: Message): boolean {
    return !!(message && message.localId && message.sent === SentStatus.NOT_SENT);
  }

  public isSentMessage(message: Message): boolean {
    return !!(message && !this.isPendingMessage(message) && message.id);
  }

  private changeDisplayName() : void{
    this.chatService.calculateDisplayName(this.chat, this.currentPlayer.id);
  }

  public pushMessageInTransform(message: Message) {
    this.transformMessages(this.messages);
  }

}
